import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { GroupedData } from '../../../util/data-util';
import { isSurfaceLike, parseRgbValue } from './minimap-color-utils';
import { RowRulerContext } from './prime-table.context';

/**
 * Manages the row ruler canvas (vertical overview strip on the right side of the table).
 * All rendering and event handling is performed outside Angular zone
 * to avoid unnecessary change-detection cycles.
 *
 * Applied directly on the `<canvas>` element (`[appRowRuler]`) — because the directive's
 * host element IS the canvas, ngOnInit/ngOnDestroy fire exactly when the *ngIf-guarded
 * canvas appears/disappears, replacing the old manual setup()/destroy() call dance.
 */
@Directive({
  selector: '[appRowRuler]',
  exportAs: 'appRowRuler'
})
export class RowRulerDirective implements OnInit, OnDestroy {

  @Input('appRowRuler') ctx!: RowRulerContext;

  // ── Color cache ───────────────────────────────────────────────────────────
  private rulerColorCache = new Map<number, string>();

  // ── Listeners / observers ─────────────────────────────────────────────────
  private rulerScrollListener: (() => void) | null = null;
  private rulerResizeObserver: ResizeObserver | null = null;

  // ── Render state ──────────────────────────────────────────────────────────
  private rulerRenderScheduled = false;

  /** requestIdleCallback id used for background color scan */
  private rulerIdleScanId = 0;

  /** Length of dataset at last scan — used to detect data changes */
  private rulerLastDataLength = -1;

  /** Static layer buffer (background + color strips); replayed via putImageData on scroll */
  private rulerStaticImage: ImageData | null = null;

  /** When true, next render must rebuild rulerStaticImage (data/size changed) */
  private rulerNeedsFullRedraw = true;

  // ── Ghost drag ────────────────────────────────────────────────────────────
  private rulerDragActive = false;
  private rulerDragGhostFrac = 0;
  private rulerDragPointerId = -1;

  // Stable arrow-fn references for addEventListener / removeEventListener
  private readonly rulerOnPointerDown = (e: PointerEvent): void => this.handlePointerDown(e);
  private readonly rulerOnPointerMove = (e: PointerEvent): void => this.handlePointerMove(e);
  private readonly rulerOnPointerUpCancel = (e: PointerEvent): void => this.handlePointerUp(e);

  private readonly canvas: HTMLCanvasElement;
  private destroyed = false;

  constructor(private el: ElementRef<HTMLCanvasElement>, private zone: NgZone) {
    this.canvas = this.el.nativeElement;
  }

  // ── Angular lifecycle ─────────────────────────────────────────────────────

  ngOnInit(): void {
    this.trySetup();
  }

  /**
   * getScrollContainer() depends on the parent's `table` ViewChild, which may not
   * be resolved yet on the very first change-detection tick (e.g. when this
   * directive's host is already in the DOM before <p-table> finishes initializing).
   * Retries for a bounded number of frames instead of relying on the caller to
   * re-trigger setup from several external call sites (the old setTimeout dance).
   */
  private trySetup(attemptsLeft = 20): void {
    if (this.destroyed) return;
    const sc = this.ctx.getScrollContainer();
    if (!sc) {
      if (attemptsLeft <= 0) return;
      requestAnimationFrame(() => this.trySetup(attemptsLeft - 1));
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.rulerScrollListener = () => this.scheduleRender();
      sc.addEventListener('scroll', this.rulerScrollListener, { passive: true });

      this.rulerResizeObserver = new ResizeObserver(() => {
        this.rulerNeedsFullRedraw = true;
        this.scheduleRender();
      });
      this.rulerResizeObserver.observe(sc);

      this.canvas.addEventListener('pointerdown', this.rulerOnPointerDown);
    });

    this.scheduleRender();
    this.startIdleScan();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    // Cancel idle scan
    if (this.rulerIdleScanId) {
      if (typeof (window as any).cancelIdleCallback === 'function')
        (window as any).cancelIdleCallback(this.rulerIdleScanId);
      else clearTimeout(this.rulerIdleScanId);
      this.rulerIdleScanId = 0;
    }

    const sc = this.ctx.getScrollContainer();
    if (this.rulerScrollListener) {
      sc?.removeEventListener('scroll', this.rulerScrollListener);
      this.rulerScrollListener = null;
    }
    this.rulerResizeObserver?.disconnect();
    this.rulerResizeObserver = null;

    this.canvas.removeEventListener('pointerdown', this.rulerOnPointerDown);

    this.rulerColorCache.clear();
    this.rulerLastDataLength = -1;
    this.rulerStaticImage = null;
    this.rulerNeedsFullRedraw = true;
  }

  // ── Public: called from component when data changes ────────────────────────

  /**
   * Invalidates the color cache so the next render triggers a full re-scan.
   * Call this when data is re-sorted or re-filtered (order changed, length may be same).
   */
  invalidateDataLength(): void {
    this.rulerLastDataLength = -1;
    this.rulerNeedsFullRedraw = true;
  }

  scheduleRender(): void {
    if (this.rulerRenderScheduled) return;
    this.rulerRenderScheduled = true;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.rulerRenderScheduled = false;
        this.render();
      });
    });
  }

  // ── Private: idle scan ────────────────────────────────────────────────────

  private startIdleScan(): void {
    const colorFn = this.ctx.rowRulerColorFn();
    if (!colorFn) return;
    const data = this.ctx.getDataArray() as any[];
    if (!data.length) return;

    if (this.rulerIdleScanId) {
      if (typeof (window as any).cancelIdleCallback === 'function')
        (window as any).cancelIdleCallback(this.rulerIdleScanId);
      else clearTimeout(this.rulerIdleScanId);
    }

    this.rulerColorCache.clear();
    this.rulerLastDataLength = data.length;
    let idx = 0;
    const CHUNK = 400;

    const scan = (): void => {
      const end = Math.min(idx + CHUNK, data.length);
      for (let i = idx; i < end; i++) {
        const item = data[i];
        // Group-header rows (grouping enabled) aren't real T rows — colorFn expects domain
        // objects and would throw/misbehave on them, and they shouldn't be colored anyway
        // (they represent collapsed/expanded group headers, not data — see also the DOM-scan
        // branch below, which already skips them via the cell-count check).
        if (item instanceof GroupedData) { this.rulerColorCache.delete(i); continue; }
        const c = colorFn(item);
        if (c) this.rulerColorCache.set(i, c);
        else this.rulerColorCache.delete(i);
      }
      idx = end;
      this.rulerNeedsFullRedraw = true;
      this.scheduleRender();

      if (idx < data.length) {
        if (typeof (window as any).requestIdleCallback === 'function')
          this.rulerIdleScanId = (window as any).requestIdleCallback(scan, { timeout: 500 });
        else
          this.rulerIdleScanId = setTimeout(scan, 0) as unknown as number;
      } else {
        this.rulerIdleScanId = 0;
      }
    };

    if (typeof (window as any).requestIdleCallback === 'function')
      this.rulerIdleScanId = (window as any).requestIdleCallback(scan, { timeout: 500 });
    else
      this.rulerIdleScanId = setTimeout(scan, 0) as unknown as number;
  }

  // ── Private: render ───────────────────────────────────────────────────────

  private render(): void {
    const canvas = this.canvas;
    const sc = this.ctx.getScrollContainer();
    if (!canvas || !sc) return;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return;

    const canvasW = canvas.offsetWidth || 16;
    if (canvasW <= 0) return;

    const rowH = this.ctx.getNavRowHeight();
    const totalScrollH = sc.scrollHeight;
    if (totalScrollH <= 0) return;
    const totalRows = Math.max(1, Math.ceil(totalScrollH / rowH));

    const containerH = (canvas.parentElement?.clientHeight || 0) || canvas.offsetHeight || 600;
    const slotH = Math.min(5, containerH / totalRows);
    const canvasH = Math.max(1, Math.round(slotH * totalRows));

    canvas.style.height = canvasH + 'px';
    if (canvas.width !== canvasW) canvas.width = canvasW;
    if (canvas.height !== canvasH) canvas.height = canvasH;

    // Detect data changes
    const colorFn = this.ctx.rowRulerColorFn();
    const currentLen = this.ctx.getDataArray().length;

    if (currentLen !== this.rulerLastDataLength && colorFn) {
      this.rulerNeedsFullRedraw = true;
      this.startIdleScan();
    } else if (!colorFn) {
      // Scan rows in CDK buffer (DOM) when no fn provided
      const tableHost = this.ctx.getTableHost();
      if (tableHost) {
        const scRect = sc.getBoundingClientRect();
        const allRows = Array.from(tableHost.querySelectorAll('tbody tr')) ;
        const displayedColumns = this.ctx.getDisplayedColumns();
        let changed = false;
        for (const row of allRows) {
          const cells = row.querySelectorAll('td, th');
          if (cells.length !== displayedColumns.length) continue;
          const rRect = row.getBoundingClientRect();
          const absTop = sc.scrollTop + rRect.top - scRect.top;
          const rowIndex = Math.round(absTop / rowH);
          if (rowIndex < 0 || rowIndex >= totalRows) continue;
          const rowStyle = getComputedStyle(row);
          const color = this.resolveRowColor(rowStyle, row);
          const prev = this.rulerColorCache.get(rowIndex);
          if (color) { if (prev !== color) { this.rulerColorCache.set(rowIndex, color); changed = true; } }
          else { if (prev !== undefined) { this.rulerColorCache.delete(rowIndex); changed = true; } }
        }
        if (changed) this.rulerNeedsFullRedraw = true;
      }
    }

    // Static layer (background + strips): rebuild or replay from cache
    const needRebuild = this.rulerNeedsFullRedraw ||
      !this.rulerStaticImage ||
      this.rulerStaticImage.width !== canvasW ||
      this.rulerStaticImage.height !== canvasH;

    if (needRebuild) {
      ctx2d.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--surface-ground').trim() || '#f8f9fa';
      ctx2d.fillRect(0, 0, canvasW, canvasH);
      for (const [rowIndex, color] of this.rulerColorCache) {
        if (rowIndex < 0 || rowIndex >= totalRows) continue;
        const y = rowIndex * slotH;
        ctx2d.fillStyle = color;
        ctx2d.fillRect(0, Math.floor(y), canvasW, Math.max(1, Math.ceil(slotH)));
      }
      this.rulerStaticImage = ctx2d.getImageData(0, 0, canvasW, canvasH);
      this.rulerNeedsFullRedraw = false;
    } else {
      ctx2d.putImageData(this.rulerStaticImage, 0, 0);
    }

    // Viewport indicator (VS Code spotlight style)
    const vpHFrac = Math.min(1, sc.clientHeight / totalScrollH);
    const vpH = Math.max(4, Math.ceil(vpHFrac * canvasH));
    const realTopFrac = sc.scrollTop / totalScrollH;
    const realVpY = Math.max(0, Math.min(Math.floor(realTopFrac * canvasH), canvasH - vpH));

    if (this.rulerDragActive) {
      const ghostTopFrac = Math.max(0, Math.min(this.rulerDragGhostFrac - vpHFrac / 2, 1 - vpHFrac));
      const ghostVpY = Math.max(0, Math.min(Math.floor(ghostTopFrac * canvasH), canvasH - vpH));

      ctx2d.fillStyle = 'rgba(0,0,0,0.28)';
      if (ghostVpY > 0) ctx2d.fillRect(0, 0, canvasW, ghostVpY);
      if (ghostVpY + vpH < canvasH) ctx2d.fillRect(0, ghostVpY + vpH, canvasW, canvasH - (ghostVpY + vpH));

      ctx2d.fillStyle = 'rgba(255,255,255,0.34)';
      ctx2d.fillRect(0, ghostVpY, canvasW, vpH);
      ctx2d.fillStyle = 'rgba(255,255,255,0.85)';
      ctx2d.fillRect(0, ghostVpY, 3, vpH);
      ctx2d.fillStyle = 'rgba(255,255,255,0.65)';
      ctx2d.fillRect(0, ghostVpY, canvasW, 1);
      if (ghostVpY + vpH < canvasH) ctx2d.fillRect(0, ghostVpY + vpH - 1, canvasW, 1);

      ctx2d.fillStyle = 'rgba(255,255,255,0.35)';
      ctx2d.fillRect(0, realVpY, canvasW, 1);
      if (realVpY + vpH < canvasH) ctx2d.fillRect(0, realVpY + vpH - 1, canvasW, 1);
      ctx2d.fillRect(0, realVpY, 2, vpH);
    } else {
      ctx2d.fillStyle = 'rgba(0,0,0,0.28)';
      if (realVpY > 0) ctx2d.fillRect(0, 0, canvasW, realVpY);
      if (realVpY + vpH < canvasH) ctx2d.fillRect(0, realVpY + vpH, canvasW, canvasH - (realVpY + vpH));

      ctx2d.fillStyle = 'rgba(255,255,255,0.28)';
      ctx2d.fillRect(0, realVpY, canvasW, vpH);

      ctx2d.fillStyle = 'rgba(255,255,255,0.72)';
      ctx2d.fillRect(0, realVpY, 3, vpH);

      ctx2d.fillStyle = 'rgba(255,255,255,0.55)';
      if (realVpY > 0) ctx2d.fillRect(0, realVpY, canvasW, 1);
      if (realVpY + vpH < canvasH) ctx2d.fillRect(0, realVpY + vpH - 1, canvasW, 1);
    }
  }

  // ── Private: pointer handlers ─────────────────────────────────────────────

  private handlePointerDown(e: PointerEvent): void {
    const canvas = this.canvas;
    const sc = this.ctx.getScrollContainer();
    if (!canvas || !sc) return;

    const rect = canvas.getBoundingClientRect();
    const canvasH = rect.height;
    if (canvasH <= 0) return;

    const y = Math.max(0, Math.min(e.clientY - rect.top, canvasH));
    this.rulerDragGhostFrac = y / canvasH;
    this.rulerDragActive = true;
    this.rulerDragPointerId = e.pointerId;

    canvas.setPointerCapture(e.pointerId);
    canvas.style.cursor = 'ns-resize';

    canvas.addEventListener('pointermove', this.rulerOnPointerMove, { passive: true });
    canvas.addEventListener('pointerup', this.rulerOnPointerUpCancel);
    canvas.addEventListener('pointercancel', this.rulerOnPointerUpCancel);

    this.scheduleRender();
  }

  private handlePointerMove(e: PointerEvent): void {
    if (!this.rulerDragActive) return;
    const canvas = this.canvas;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const canvasH = rect.height;
    if (canvasH <= 0) return;

    const y = Math.max(0, Math.min(e.clientY - rect.top, canvasH));
    this.rulerDragGhostFrac = y / canvasH;
    this.scheduleRender();
  }

  private handlePointerUp(_e: PointerEvent): void {
    if (!this.rulerDragActive) return;
    this.rulerDragActive = false;

    const canvas = this.canvas;
    if (canvas) {
      canvas.removeEventListener('pointermove', this.rulerOnPointerMove);
      canvas.removeEventListener('pointerup', this.rulerOnPointerUpCancel);
      canvas.removeEventListener('pointercancel', this.rulerOnPointerUpCancel);
      try { canvas.releasePointerCapture(this.rulerDragPointerId); } catch (_) { /* ignore */ }
      canvas.style.cursor = '';
    }

    const sc = this.ctx.getScrollContainer();
    if (sc) {
      const targetTop = this.rulerDragGhostFrac * sc.scrollHeight - sc.clientHeight / 2;
      sc.scrollTop = Math.max(0, Math.min(targetTop, sc.scrollHeight - sc.clientHeight));
    }
  }

  // ── Private: color resolution ─────────────────────────────────────────────

  private resolveRowColor(style: CSSStyleDeclaration, el?: Element): string {
    const ALPHA = 0.85;
    const isHovered = !!el?.matches?.(':hover');
    const isSelected = !!el?.matches?.('.p-highlight, [aria-selected="true"]');

    const color = this.resolveStyle(style, isHovered, isSelected, ALPHA);
    if (color) return color;

    if (el) {
      const cells = el.querySelectorAll('td');
      for (let i = 0; i < Math.min(cells.length, 4); i++) {
        const cellStyle = getComputedStyle(cells[i]);
        const cellColor = this.resolveStyle(cellStyle, isHovered, isSelected, ALPHA);
        if (cellColor) return cellColor;
      }
    }
    return '';
  }

  private resolveStyle(style: CSSStyleDeclaration, isHovered: boolean, isSelected: boolean, alpha: number): string {
    // 1. backgroundColor
    const bg = style.backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      if (!isHovered || isSelected) {
        const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/.exec(bg);
        if (m) {
          const r = Math.round(parseFloat(m[1]));
          const g = Math.round(parseFloat(m[2]));
          const b = Math.round(parseFloat(m[3]));
          if (!isSurfaceLike(`rgb(${r},${g},${b})`))
            return `rgba(${r},${g},${b},${alpha})`;
        }
      }
    }

    // 2. CSS custom property --c-grad1
    const grad1 = style.getPropertyValue('--c-grad1').trim();
    if (grad1) {
      const m = /color-mix\(\s*in\s+srgb\s*,\s*(.+?)\s*,\s*transparent/i.exec(grad1);
      if (m) {
        let base = m[1].trim();
        const varRef = /var\(\s*(--[\w-]+)\s*\)/.exec(base);
        if (varRef) base = style.getPropertyValue(varRef[1]).trim();
        const rgb = parseRgbValue(base);
        if (rgb && !isSurfaceLike(`rgb(${rgb.r},${rgb.g},${rgb.b})`))
          return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
      }
    }

    // 3. background-image gradient
    const img = style.backgroundImage;
    if (img && img !== 'none') {
      const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+%?))?\s*\)/.exec(img);
      if (m) {
        let r = parseFloat(m[1]); let g = parseFloat(m[2]); let b = parseFloat(m[3]);
        const aRaw = m[4] ?? '1'; let a = parseFloat(aRaw);
        if (aRaw.endsWith('%')) a /= 100;
        if (a > 0 && a < 0.5) {
          r = Math.min(255, r / a);
          g = Math.min(255, g / a);
          b = Math.min(255, b / a);
        }
        if (!isSurfaceLike(`rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`))
          return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${alpha})`;
      }
    }

    return '';
  }
}
